import React from 'react';
import { IconButton, FileUpload } from '@lucid/lego';
import { useUploadOrderDocument } from '../../../../../api/services/lupp/order/order';
import { useFileUpload } from '../../../../../hooks/useFileUpload';
import { FILEHUB_MODULE_NAME } from '../../../../../constants/constants';
import type { DocumentCategory } from '../../../../../constants/constants';
import type { OrderDetailsResponse } from '../../../../../api/services/lupp/order/dto.order';
import type { InitialFileReference } from '../../../../../api/services/lupp/order/transformers.order';
import styles from './QualityCheck.module.scss';

/**
 * Props for the CategoryUploadRow component
 * @property category - Document category for the upload
 * @property orderData - Order details for metadata
 * @property initialFiles - Pre-existing files to display
 * @property label - Display label for the upload row
 * @property theme - Visual theme ('light' or 'dark')
 * @property labels - Localized labels for i18n
 * @property dataTestId - Test ID for the upload row
 * @property onDocumentClick - Handler for document download clicks
 * @property onError - Handler for upload/download errors
 */
type CategoryUploadRowProps = {
  category: DocumentCategory;
  orderData: OrderDetailsResponse;
  initialFiles: InitialFileReference[];
  label: string;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId: string;
  onDocumentClick: (fileId: string, fileName: string) => void;
  onError: (errorMessage: string) => void;
  removable?: boolean;
  onRemoveRow?: () => void;
};

/**
 * A single file upload row for a specific document category.
 * Encapsulates its own useFileUpload hook call, allowing it to be
 * rendered dynamically in a list without breaking React's rules of hooks.
 *
 * @component
 * @param props - Component props
 * @returns Inline FileUpload for the given category
 * @example
 * ```tsx
 * <CategoryUploadRow
 *   category={DOCUMENT_CATEGORY.FINAL_QUALITY_CHECK_OTHER}
 *   orderData={orderDetails}
 *   initialFiles={files}
 *   label="Final Quality Check - Other 1"
 *   theme="dark"
 *   labels={labels}
 *   dataTestId="quality-check-additional-0"
 *   onDocumentClick={handleDocumentClick}
 *   onError={setErrorMessage}
 * />
 * ```
 */
const CategoryUploadRow: React.FC<CategoryUploadRowProps> = ({
  category,
  orderData,
  initialFiles,
  label,
  theme,
  labels,
  dataTestId,
  onDocumentClick,
  onError,
  removable = false,
  onRemoveRow,
}) => {
  const uploadOrderDocument = useUploadOrderDocument();

  const { getUploadUrl, handleFileRemove } = useFileUpload({
    customerId: orderData.orderIdentifier,
    category,
    entityType: FILEHUB_MODULE_NAME.ORDER_MANAGEMENT,
    entityId: orderData.orderIdentifier,
    sfdcParentIdentifier: orderData.sfdcOrderIdentifier ?? undefined,
    onUploadUrlSuccess: (response) => {
      uploadOrderDocument.mutate(
        {
          orderIdentifier: orderData.orderIdentifier,
          moduleName: 'Order Management',
          moduleReferenceIdentifier: orderData.orderIdentifier,
          fileId: response.fileId || '',
          tenantId: response.tenantId || '',
          customerId: response.customerId || orderData.orderIdentifier,
          category: category satisfies DocumentCategory,
          fileNameOriginal: response.fileNameOriginal || '',
          fileNameSanitized: response.fileNameSanitized || '',
          mimeType: response.mimeType || 'application/pdf',
          createdAt: response.createdAt || new Date().toISOString(),
          sfdcDeliveryIdentifier: orderData.sfdcDeliveryIdentifier ?? undefined,
        },
        {
          onSuccess: () => onError(''),
          onError: () => onError(labels['upload.error'] || ''),
        }
      );
    },
    onError: () => {
      onError(labels['upload.error'] || '');
    },
  });

  return (
    <div className={styles.uploadRow}>
      <div className={styles.uploadRowContent}>
        <FileUpload
          dataTestId={dataTestId}
          theme={theme === 'dark' ? 'light' : 'dark'}
          variant="inline"
          getUploadUrl={getUploadUrl}
          onFileRemove={handleFileRemove}
          accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.mp4,.heic"
          label={label}
          uploadButtonLabel={labels?.upload}
          helperText={
            labels?.['qualityCheck.fileUploadHelper'] ||
            'JPG, PNG, GIF, PDF, DOC, MP4, HEIC files supported (max 10 MB each)'
          }
          initialFiles={initialFiles}
          removable={removable}
          onDocumentClick={onDocumentClick}
        />
      </div>
      {onRemoveRow && initialFiles.length === 0 && (
        <div className={styles.uploadRowAction}>
          <IconButton
            dataTestId={`${dataTestId}-remove`}
            gaComponent="CategoryUploadRow"
            buttonType="tertiary"
            color="error-1"
            size="small"
            theme={theme === 'dark' ? 'light' : 'dark'}
            icon={{ category: 'global', name: 'circle-close' }}
            handleClick={onRemoveRow}
          />
        </div>
      )}
    </div>
  );
};

CategoryUploadRow.displayName = 'CategoryUploadRow';

export default CategoryUploadRow;
